/**
 * 판매자배송 판매장부 계산 — MarketplaceSales 줄(Open API 매출 인식 내역을 옵션 줄로 편 것) → 날짜별·옵션별 합.
 * 안 모은 날은 0 이 아니라 null(«안 모음») — 매출 0 인 날과 가른다(서버 MarketplaceSalesCollected).
 * 환불(saleType REFUND)은 금액·수량 부호가 어떻게 오든 뺀다.
 */

const DAY_MS = 24 * 60 * 60 * 1000

function daysBetween(start, end) {
    const out = []
    for (let t = Date.parse(start + 'T00:00:00Z'); t <= Date.parse(end + 'T00:00:00Z'); t += DAY_MS) {
        out.push(new Date(t).toISOString().slice(0, 10))
    }
    return out
}

/** [start, end] 를 maxDays 일 묶음으로 쪼갠다 — Open API 한 번에 받는 기간 상한용 */
export function splitRange(start, end, maxDays) {
    const days = daysBetween(start, end)
    const out = []
    for (let i = 0; i < days.length; i += maxDays) out.push([days[i], days[Math.min(i + maxDays, days.length) - 1]])
    return out
}

/**
 * 쿠팡에서 모을 수 있는 기간 — Open API 매출 내역은 어제까지만 받는다(오늘을 넣으면 400 «To date must be before or equal to yesterday»).
 * 끝날을 어제로 자른다(cut) · 시작이 오늘 이후면 모을 것 없음(null)
 */
export function collectableRange(start, end, today) {
    const yesterday = new Date(Date.parse(today + 'T00:00:00Z') - DAY_MS).toISOString().slice(0, 10)
    const to = end > yesterday ? yesterday : end
    if (start > to) return null
    return { from: start, to, cut: to !== end }
}

function signed(row, field) {
    const v = Number(row[field]) || 0
    return row.saleType === 'REFUND' ? -Math.abs(v) : v
}

export function buildMarketplaceLedger(sales, collectedDates, start, end) {
    const collected = new Set(collectedDates || [])
    const byDay = {}
    const byItem = {}
    for (const r of sales || []) {
        const d = byDay[r.recognitionDate] || (byDay[r.recognitionDate] = { saleAmount: 0, settlementAmount: 0, quantity: 0, orders: new Set() })
        d.saleAmount += signed(r, 'saleAmount')
        d.settlementAmount += signed(r, 'settlementAmount')
        d.quantity += signed(r, 'quantity')
        if (r.saleType !== 'REFUND') d.orders.add(String(r.orderId))
        const key = String(r.vendorItemId)
        const it = byItem[key] || (byItem[key] = { vendorItemId: key, vendorItemName: r.vendorItemName || '', saleAmount: 0, settlementAmount: 0, quantity: 0 })
        it.saleAmount += signed(r, 'saleAmount')
        it.settlementAmount += signed(r, 'settlementAmount')
        it.quantity += signed(r, 'quantity')
    }

    const days = daysBetween(start, end).reverse().map((date) => {
        if (!collected.has(date)) return { date, collected: false, saleAmount: null, settlementAmount: null, quantity: null, orders: null }
        const d = byDay[date]
        return { date, collected: true, saleAmount: d ? d.saleAmount : 0, settlementAmount: d ? d.settlementAmount : 0, quantity: d ? d.quantity : 0, orders: d ? d.orders.size : 0 }
    })

    const got = days.filter((d) => d.collected)
    const sum = (f) => (got.length ? got.reduce((a, d) => a + d[f], 0) : null)
    return {
        days,
        items: Object.values(byItem).sort((a, b) => b.saleAmount - a.saleAmount),
        summary: {
            saleAmount: sum('saleAmount'),
            settlementAmount: sum('settlementAmount'),
            quantity: sum('quantity'),
            collectedDays: got.length,
            missingDays: days.length - got.length,
        },
    }
}
